import { ButtonBase, Typography } from '@material-ui/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link as RouterLink } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import React from 'react';

/*
  Instructions: Lay out NavigationTiles inside a NavigationTileGrid, which shows one column on phones, two from sm, and three
  from md.

  NavigationTile props:
    - icon: a FontAwesome icon (e.g. ['fas', 'user-plus']). Register it in utilities/iconLoader.
    - title: the tile's name, shown next to the icon in the header. Long titles are cut off so every header is one line.
    - description (optional): what the tile leads to, shown in the body.
    - to (optional): the route the tile links to.
    - onClick (optional): runs when the tile is clicked, for tiles that don't navigate.
*/

const useStyles = makeStyles(theme => ({
  grid: {
    display: 'grid',
    gap: theme.spacing(2),
    gridTemplateColumns: 'minmax(0, 1fr)',
    [theme.breakpoints.up('sm')]: {
      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    },
    [theme.breakpoints.up('md')]: {
      gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    },
  },
  tile: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    justifyContent: 'flex-start',
    width: '100%',
    minHeight: 200,
    textAlign: 'left',
    textDecoration: 'none',
    color: theme.palette.text.primary,
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.shadows[1],
    transition: theme.transitions.create(['border-color', 'box-shadow']),
    '&:hover': {
      borderColor: theme.palette.primary.main,
      boxShadow: theme.shadows[4],
    },
  },
  tileFocusVisible: {
    borderColor: theme.palette.primary.main,
    boxShadow: theme.shadows[4],
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    padding: theme.spacing(2, 2.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  icon: {
    color: theme.palette.primary.main,
  },
  title: {
    minWidth: 0,
  },
  body: {
    flexGrow: 1,
    padding: theme.spacing(2, 2.5),
  },
}));

export const NavigationTileGrid = ({ children }) => {
  const classes = useStyles();

  return <div className={classes.grid}>{children}</div>;
};

NavigationTileGrid.propTypes = {
  children: PropTypes.node.isRequired,
};

export const NavigationTile = ({ icon, title, description, to, onClick }) => {
  const classes = useStyles();
  const linkProps = to ? { component: RouterLink, to } : {};

  return (
    <ButtonBase className={classes.tile} focusVisibleClassName={classes.tileFocusVisible} onClick={onClick} {...linkProps}>
      <div className={classes.header}>
        <FontAwesomeIcon className={classes.icon} icon={icon} size="2x" fixedWidth />
        <Typography className={classes.title} variant="h6" component="span" noWrap>
          {title}
        </Typography>
      </div>
      {description && (
        <div className={classes.body}>
          <Typography variant="body1" color="textSecondary">
            {description}
          </Typography>
        </div>
      )}
    </ButtonBase>
  );
};

NavigationTile.propTypes = {
  icon: PropTypes.oneOfType([PropTypes.array, PropTypes.object, PropTypes.string]).isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.node,
  to: PropTypes.string,
  onClick: PropTypes.func,
};
